import { AxiosResponse } from 'axios'
import { queryOptions } from '@tanstack/react-query'
import { createUrl } from './helpers'
import axiosAuth from './config'
import { NFaqs } from '../interfaces/services/faqs.interfaces'
import { separateSectionsAndQuestions } from '../state/stores/faqs'

export const getFaqsPerSociety = (
  societyId: string
): Promise<AxiosResponse<NFaqs.NGetPerSocieties.IResponse>> => {
  const body: NFaqs.NGetPerSocieties.IRequestBody = {
    societyIds: [societyId],
  }
  return axiosAuth.post<NFaqs.NGetPerSocieties.IResponse>(
    createUrl('/faqs/get-per-societies/'),
    body
  )
}

export const getFaq = (
  id: string
): Promise<AxiosResponse<NFaqs.NGetById.IResponse>> => {
  return axiosAuth.get<NFaqs.NGetById.IResponse>(createUrl(`/faqs/faq/${id}`))
}

export const patchFaq = (variables: {
  id: string
  body: NFaqs.NPatch.IRequestBody
}): Promise<AxiosResponse<NFaqs.NPatch.IResponse>> => {
  return axiosAuth.patch<NFaqs.NPatch.IResponse>(
    createUrl(`/faqs/faq/${variables.id}`),
    variables.body
  )
}

export const createFaq = (
  body: NFaqs.NCreate.IRequestBody
): Promise<AxiosResponse<NFaqs.NCreate.IResponse>> => {
  return axiosAuth.post<NFaqs.NCreate.IResponse>(createUrl(`/faqs/faq`), body)
}

export const deleteFaq = (
  id: string
): Promise<AxiosResponse<NFaqs.NDel.IResponse>> => {
  return axiosAuth.delete<NFaqs.NDel.IResponse>(createUrl(`/faqs/faq/${id}`))
}

/**
 * Renames a section. Questions are linked to their section only by the
 * `section` string, so they have to be renamed too or they are left behind
 * under the old name and show up as a second, duplicate section.
 */
export const patchFaqSection = async (variables: {
  id: string
  body: NFaqs.NPatch.IRequestBody
  questionIds: string[]
}): Promise<AxiosResponse<NFaqs.NPatch.IResponse>> => {
  const resp = await patchFaq({ id: variables.id, body: variables.body })

  // The section representative may itself be a question document when no
  // section document exists; it has already been patched above.
  const questionIds = variables.questionIds.filter((id) => id !== variables.id)

  if (variables.body.section && questionIds.length > 0) {
    await Promise.all(
      questionIds.map((questionId) =>
        patchFaq({
          id: questionId,
          body: { section: variables.body.section },
        })
      )
    )
  }

  return resp
}

/**
 * Deletes a section together with its questions. See patchFaqSection for why
 * the questions have to be handled explicitly.
 */
export const deleteFaqSection = async (variables: {
  id: string
  questionIds: string[]
}): Promise<void> => {
  await deleteFaq(variables.id)

  const questionIds = variables.questionIds.filter((id) => id !== variables.id)

  if (questionIds.length > 0) {
    await Promise.all(questionIds.map((questionId) => deleteFaq(questionId)))
  }
}

export const faqQueries = {
  getFaqsPerSociety: (societyId?: string) =>
    queryOptions({
      enabled: !!societyId,
      queryKey: [{ scope: 'faq', entity: 'list', societyId }],
      queryFn: async ({ queryKey }) => {
        const { data } = (await getFaqsPerSociety(queryKey[0].societyId!)).data
        return separateSectionsAndQuestions(data)
      },
    }),
}
